
import {
  defineComponent,
  computed,
  onMounted,
  SetupContext,
  toRefs,
  PropType
} from "vue";
import MyListFolderList from "@/components/favorites/MyListFolderList.vue";
import CommonHorizontalRecipeList from "@/components/commonRecipe/CommonHorizontalRecipeList.vue";
import { MypageRecipeClip, FolderInfo } from "@/types/mypage";
import { RecipeFoodItem } from "@/types/pantry";
import NativeClient from "@/client/native-client";
import router from "@/router";
import { createMypageFoodsToCommonRecipes, createMypageRecipesToCommonRecipes } from "@/utils/mypage-util";
import { PAGE_ID } from "@/const/log-const";
import { BeaconLogInfo } from "@/types/common";
import store from "@/store";
  
export default defineComponent({
  name: "MypageFavorites",
  components: { MyListFolderList, CommonHorizontalRecipeList },
  props: {
    /** お気に入り食材 */
    foodList: {
       type: Array as PropType<RecipeFoodItem[]>,
       default: () => null
    },
    recipeList: {
      type: Array as PropType<MypageRecipeClip[]>,
      default: () => null
    },
    /** フォルダーリスト */
    folderList: {
      type: Array as PropType<FolderInfo[]>,
      default: () => null
    }
  },
  setup(props, context: SetupContext) {
    NativeClient.sendDebugLog("MypageFavorites setup");
  
    // テキスト
    const favoriteFoodTitle = "お気に入り食材";
    const favoriteFoodReferenceTitle = "もっと見る";
    const favoriteRcipeTitle = "お気に入りレシピ";
    const recipeAllReferenceTitle = "もっと見る";
    const folderTitle = "レシピフォルダ";
    const folderEditTitle = "フォルダを編集⋯";
    const nothingFoodsMessage = "食材をお気に入り登録すると<br>表示されます";
    const nothingRecipeMessage = "レシピをお気に入り登録すると<br>表示されます";
    const appendFolderButtonTitle = "新しいフォルダを追加";
    const nothingFolderMessage = "＋ボタンで追加して10個まで登録できます。";
  
    // ボタン等のイベント処理
    const selectFolder = (id: number, name: string) => {
      // 対応カテゴリー詳細の画面へ遷移
      NativeClient.sendDebugLog("MypageFavorites page transition = /mypage-favorites-folder?folderId=" + id + '&folderName=' + name);
      const encodedName = encodeURIComponent(name);
      store.dispatch("mypage/updateFolderName", name);
      router.push(`/mypage-folder?folderId=` + id + '&folderName=' + encodedName);
    };
    const selectFood =  (foodId: number, type: string) => {
      // 食材詳細（PR）
      NativeClient.sendDebugLog("MypageFavorites selectFood() foodId = " + foodId + ", name = " + type);
      NativeClient.transitionPantryPRFood(`${foodId}`);
    };
    const selectRecipe = (recipeId: number, type: string) => {
      // 最近追加したレシピに遷移
      NativeClient.sendDebugLog("MypageFavorites recipeId = " + recipeId + ", type = " + type);

      // Beacon Log
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, "btn_1_E-08_MylistRecipe", [String(recipeId)]);
      NativeClient.sendClickedActionLog(logInfo);

      NativeClient.transitionReceipDetail(recipeId, type);
    };
    const selectFoodList = () => {
      // お気に入り食材へ
      router.push(`/mypage-foods`);
    };
    const selectRecipeList = () => {
      // お気に入りレシピへ
      router.push(`/mypage-folder`);
    };
    const selectAppendFolder = () => {
      NativeClient.sendDebugLog("MypageFavorites append folder");
      context.emit('append-folder');
    };
    const moveFolder = (folderId: number, position: number) => {
      NativeClient.sendDebugLog("MypageFavorites move folder list folderId = " + folderId + ", position = " + position);
      context.emit('move-folder', folderId, position);
    }

    // 表示/非表示設定
    const styleAllFoodListButton = computed(() => {
      NativeClient.sendDebugLog("MypageFavorites styleAllFoodListButton");
      if (props.foodList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleAllFoodListButton foodList null branch");
        return { visibility: `hidden` };
      } else if (props.foodList.length == 0) {
        return { visibility: `hidden` };
      }
      return { visibility: `visible` };
    });
    const styleAllRecipeListButton = computed(() => {
      NativeClient.sendDebugLog("MypageFavorites styleAllRecipeListButton");
      if (props.recipeList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleAllRecipeListButton recipeList null branch");
        return { visibility: `hidden` };
      } else if (props.recipeList.length == 0) {
        return { visibility: `hidden` };
      }
      return { visibility: `visible` };
    });
    const styleFoodList = computed(() => {
      if (props.foodList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleFoodList hidden");
        return { display: `none` };
      } else if (props.foodList.length == 0) {
        NativeClient.sendDebugLog("MypageFavorites styleFoodList foodList has no element");
        return { display: `none` };
      }
      NativeClient.sendDebugLog("MypageFavorites styleFoodList normal");
      return { display: `inline-box` };
    });
    const styleRecipeList = computed(() => {
      if (props.recipeList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleRecipeList hidden");
        return { display: `none` };
      } else if (props.recipeList.length == 0) {
        NativeClient.sendDebugLog("MypageFavorites styleRecipeList recipeList has no element");
        return { display: `none` };
      }
      NativeClient.sendDebugLog("MypageFavorites styleRecipeList normal");
      return { display: `inline-box` };
    });
    const styleFoodListNothing = computed(() => {
      if (props.foodList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleFoodListNothing hidden");
        return { display: `inline-box`, visibility: `hidden` };
      } else if (props.foodList.length == 0) {
        NativeClient.sendDebugLog("MypageFavorites styleFoodListNothing visible");
        return { display: `inline-box`, visibility: `visible` };
      }
      NativeClient.sendDebugLog("MypageFavorites styleFoodListNothing none");
      return { display: `none` };
    });
    const styleRecipeListNothing = computed(() => {
      if (props.recipeList == null) {
        NativeClient.sendDebugLog("MypageFavorites styleRecipesNothing hidden");
        return { display: `inline-box`, visibility: `hidden` };
      } else if (props.recipeList.length == 0) {
        NativeClient.sendDebugLog("MypageFavorites styleRecipesNothing visible");
        return { display: `inline-box`, visibility: `visible` };
      }
      NativeClient.sendDebugLog("MypageFavorites styleRecipesNothing none");
      return { display: `none` };
    });
    const styleAppendFolder = computed(() => {
      NativeClient.sendDebugLog("MypageFavorites styleAppendFolder");
      if (props.folderList == null) {
        NativeClient.sendDebugLog("MypageFavorites beforeReceivedFolderList branch");
        return { visibility: 'hidden' };
      }
      return { visibility: `visible` };
    });
    const styleFolderList = computed(() => {
      if (props.folderList == null) {
        return { display: `none` };
      } else if (props.folderList.length == 0) {
        return { display: `none` };
      }
      return { display: `block` };
    });
    const styleFolderListNothing = computed(() => {
      if (props.folderList == null) {
        return { display: `inline-box`, visibility: `hidden` };
      } else if (props.folderList.length == 0) {
        return { display: `inline-box`, visibility: `visible` };
      }
      return { display: `none` };
    });
  
    const displayFoodList = computed(() => {
      // お気に入り食材リストをFavoritesHistoriesHorizontalRecipeList用のものに切り替える
      if (props.foodList == null) {
        NativeClient.sendDebugLog("MypageFavorites foodList is null");
        return null;
      }
      NativeClient.sendDebugLog("MypageFavorites foodList isn't null");
      const allList = createMypageFoodsToCommonRecipes(props.foodList);
      return allList;
    });
    const displayRecipeList = computed(() => {
      // お気に入りレシピのリストをFavoritesHistoriesHorizontalRecipeList用のものに切り替える
      if (props.recipeList == null) {
        NativeClient.sendDebugLog("MyList allList recipeList is null");
        return null;
      }
      NativeClient.sendDebugLog("MyList allList recipeList isn't null");
      const allList = createMypageRecipesToCommonRecipes(props.recipeList);
      return allList;
    });

    // フォルダ一覧
    const displayFolderList = computed(() => {
      // フォルダ一位欄を変換する
      const displayFolderList: FolderInfo[] = [];
      if (props.folderList == null) {
        return [];
      }
      for (const folderElement of props.folderList) {
        const element = new FolderInfo();
        element.id = folderElement.id;
        element.name = folderElement.name;
        element.image = folderElement.image;
        element.numberOfItems = folderElement.numberOfItems;
        displayFolderList.push(element);
      }
      return displayFolderList
    });

    return {
      favoriteFoodTitle,
      favoriteFoodReferenceTitle,
      favoriteRcipeTitle,
      recipeAllReferenceTitle,
      folderTitle,
      folderEditTitle,
      displayFoodList,
      displayRecipeList,
      displayFolderList,
      selectFood,
      selectFolder,
      selectRecipe,
      selectFoodList,
      selectRecipeList,
      moveFolder,
      nothingFoodsMessage,
      nothingRecipeMessage,
      nothingFolderMessage,
      appendFolderButtonTitle,
      selectAppendFolder,
      styleAllFoodListButton,
      styleAllRecipeListButton,
      styleFoodList,
      styleFoodListNothing,
      styleRecipeList,
      styleRecipeListNothing,
      styleAppendFolder,
      styleFolderList,
      styleFolderListNothing
    };
  }
});
